export const AUTHORIZE_ENDPOINT = '/connect/authorize';

const PENDING_AUTHORIZATION_KEY = 'pendingAuthorization';
const PENDING_AUTHORIZATION_LIFETIME_MS = 10 * 60 * 1000;

export type AuthorizationParameters = Record<string, string>;

type StoredAuthorization = {
  parameters: AuthorizationParameters;
  createdAt: number;
};

export const parseAuthorizationReturnUrl = (
  returnUrl: string | null
): AuthorizationParameters | null => {
  if (!returnUrl?.startsWith(`${AUTHORIZE_ENDPOINT}?`)) {
    return null;
  }

  const parameters = Object.fromEntries(
    new URLSearchParams(returnUrl.substring(AUTHORIZE_ENDPOINT.length + 1))
  );
  delete parameters.access_token;
  delete parameters.deny;

  return parameters.client_id ? parameters : null;
};

export const capturePendingAuthorization = (search: string, now: number = Date.now()) => {
  const parameters = parseAuthorizationReturnUrl(new URLSearchParams(search).get('returnUrl'));
  if (!parameters) {
    return;
  }

  const stored: StoredAuthorization = { parameters, createdAt: now };
  try {
    sessionStorage.setItem(PENDING_AUTHORIZATION_KEY, JSON.stringify(stored));
  } catch {
    return;
  }
};

export const getPendingAuthorization = (
  now: number = Date.now()
): AuthorizationParameters | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_AUTHORIZATION_KEY);
    if (!raw) {
      return null;
    }

    const stored = JSON.parse(raw) as StoredAuthorization;
    if (now - stored.createdAt > PENDING_AUTHORIZATION_LIFETIME_MS) {
      sessionStorage.removeItem(PENDING_AUTHORIZATION_KEY);
      return null;
    }

    return stored.parameters;
  } catch {
    return null;
  }
};

export const clearPendingAuthorization = () => {
  try {
    sessionStorage.removeItem(PENDING_AUTHORIZATION_KEY);
  } catch {
    return;
  }
};

const postToAuthorizeEndpoint = (amApiUrl: string, fields: AuthorizationParameters) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = `${amApiUrl.replace(/\/$/, '')}${AUTHORIZE_ENDPOINT}`;

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
};

export const approveAuthorization = (
  amApiUrl: string,
  parameters: AuthorizationParameters,
  accessToken: string
) => postToAuthorizeEndpoint(amApiUrl, { ...parameters, access_token: accessToken });

export const denyAuthorization = (amApiUrl: string, parameters: AuthorizationParameters) =>
  postToAuthorizeEndpoint(amApiUrl, { ...parameters, deny: 'true' });
